import { t } from '@lingui/core/macro';

/**
 * The error codes the AI backend can send, and what to say about each.
 *
 * The backend sends a stable `code` next to its human-readable `error`, so the
 * wording lives here where it can be translated rather than arriving as English
 * from the server. A code this build does not know about falls back to the
 * server's own message, which is wrong in the user's language but is better
 * than inventing a reason.
 *
 * Adding a code to the backend means adding it here too; the set is small
 * enough to keep in step by hand.
 */
const SERVER_ERROR_CODES = [
  'chat_forbidden',
  'rate_limited',
  'conversation_not_found',
  'storage_unavailable',
  'tool_budget_exceeded',
  'unknown_tool',
] as const;

/**
 * The translated message for a server error code, or `undefined` when the code
 * is not one this build knows about.
 *
 * The lookup happens on demand rather than in a table built at module load:
 * `t` throws if it is called before a locale is active, and a module-level
 * table would be built before the app had activated one.
 */
export function translateServerError(code: unknown): string | undefined {
  if (typeof code !== 'string') return undefined;
  if (!SERVER_ERROR_CODES.includes(code as (typeof SERVER_ERROR_CODES)[number])) {
    return undefined;
  }

  switch (code) {
    case 'chat_forbidden':
      return t`AI chat is restricted to admin, providerAdmin or providerUser.`;
    case 'rate_limited':
      return t`Rate limit exceeded. Please wait before sending more messages.`;
    case 'conversation_not_found':
      return t`Conversation not found`;
    case 'storage_unavailable':
      return t`Conversation storage unavailable`;
    case 'tool_budget_exceeded':
      return t`The reply used every available tool call. Please ask a more specific question.`;
    case 'unknown_tool':
      return t`The assistant asked for a tool that does not exist.`;
    default:
      return undefined;
  }
}
